// Konfigurace a guardy vlastního vyhledávání (issue #67, epic #62).
//
// Jediné místo, které rozhoduje, jestli se naše vrstva vůbec spustí. Vrací
// `null` = **Ú0 z koncepce §Fallback**: nula requestů, nativní Shoptet chování
// 1:1. Volající se nikdy neptá „proč", jen degraduje.
//
// ⚠️ OPRAVA KONCEPCE (ověřeno na dev instanci 2026-08-03): koncepce §Dvě domény
// mluví o `shoptetData('projectId')`, ale `window.shoptetData` na Shoptetu
// **neexistuje**. Zdroj pravdy je `window.dataLayer[0].shoptet` — serverově
// vyrenderovaný, takže je k dispozici dřív než náš JS:
//   { projectId: 797093, language: 'cs',
//     customer: { priceListId: 1, priceRatio: 1, groupId: null, registered: false } }

import SHOP from '../../config/shop.js';

const API = SHOP.search.api;

// projectId → tenant v `search/data/tenants.json`.
//
// Tenanti jsou od #202 dva: `jipos-cz` = ostrá (indexovaná LIVE tokenem,
// URL karet vedou na www.jipos.cz), `jipos-dev` = dev instance s vlastním
// indexem, na kterém se dál ověřují dávky W2 a popisů. Na ostré vrstvu drží
// vypnutou stand-down guard (Luigi's Box na stránce) — mapa samotná ji
// nezapíná; master vypínač dne D je sundání LB skriptu (#195).
// Rychlé přepnutí bez deploye: `window.__BS_SEARCH__`.
// Exportováno kvůli unit testu prefetch snippetu hlavičky (tools/unit/
// search-prefetch.test.mjs) — snippet nese vlastní kopii mapy.
export const TENANTS = SHOP.search.tenants; // manifest shopu (src/config/bs.config.*.mjs)

// Shozená vrstva (5xx z API) přežívá v sessionStorage jako čas shození: do
// `BREAKER_TTL_MS` je vrstva vypnutá, pak se guardy přepočítají a první GET jde
// bez HTTP cache (`takeBreakerProbe()`). Do 2026-09-15 držel klíč `'1'` na celý
// panel prohlížeče — sekundové okno 502 při restartu API tak vypínalo hledání
// zákazníkovi na celou návštěvu (sweep `ops/sweeps/2026-09-15-vyhledavani-…`).
// Hodnota `'1'` z doby před TTL se čte jako prošlá.
const BREAKER_KEY = 'bs-search-off';
export const BREAKER_TTL_MS = 5 * 60 * 1000;

let resolved;
let resolvedDone = false;
let indexResolved;
let indexDone = false;
let wholesaleResolved;
let wholesaleDone = false;
// Čas shození breakeru v PAMĚTI stránky. Dvě role: (1) cache guardů drží `null`
// kvůli breakeru → po TTL se musí zneplatnit; (2) pojistka, když sessionStorage
// nejde (blokované úložiště, plná kvóta) — bez ní by `breakerTripped()` vrátil
// `false` a vrstva by se hned po shození znovu zapnula (Codex review PR #377).
// Klíč z úložiště (shození na předchozí stránce) se sem při čtení promítne.
let trippedAt = 0;
let probePending = false;

/** Serverem vyrenderovaný blok Shoptetu. `null`, když dataLayer ještě není. */
function shoptetData() {
  try {
    const first = window.dataLayer && window.dataLayer[0];
    return (first && first.shoptet) || null;
  } catch (e) {
    return null;
  }
}

/** Chybí něco, bez čeho se vrstva psát nedá (staré prohlížeče). */
function unsupported() {
  return (
    typeof window.fetch !== 'function' ||
    typeof window.AbortController !== 'function' ||
    typeof window.URL !== 'function' ||
    typeof window.Map !== 'function'
  );
}

/**
 * Offline fixture (`dev/*.html` přes `file://`) — vrstva se nespouští.
 *
 * Koncepce §API kontrakt počítala s tím, že CORS-simple GET půjde mockovat
 * i z `file://`. Nejde: prohlížeč posílá `Origin: null` a API má allowlist
 * konkrétních originů, takže request skončí na CORS chybě v konzoli.
 * Offline fixtures tedy testují **fallback cestu** (a to je správně — je to
 * jediná cesta, která offline dává smysl); API cesta se ověřuje živě.
 */
function offlineFixture() {
  return window.location.protocol !== 'http:' && window.location.protocol !== 'https:';
}

/**
 * Stand-down guard: běží na stránce Luigi's Box? Pak se nespouštíme.
 * Díky tomu jde build nasadit na ostrou **ještě před vypnutím LB** a přepnout
 * se pouhým smazáním jejich skriptu v adminu — a stejně tak vrátit zpátky.
 *
 * ⚠️ Ověřeno jen negativně: na dev instanci LB neběží (žádná ze tří signatur),
 * takže pozitivní větev čeká na test proti prostředí, kde LB reálně je.
 */
//
// Signatura je úzká (review 2026-09-09, B3-06): dřívější `[id^="lb-"]`
// a `[class*="luigi"]` by vrstvu tiše vypnuly kvůli libovolnému lightboxu
// nebo doplňku s takovým id. Důvod vypnutí se zapisuje do
// `window.__BS_SEARCH_OFF__`, aby šel stav ověřit sondou.
function luigisBoxPresent() {
  try {
    let reason = null;
    if (window.LuigisBoxTracker) reason = 'luigisbox:tracker';
    else if (document.querySelector('.luigi-ac-autocomplete')) reason = 'luigisbox:autocomplete';
    else if (
      Array.prototype.some.call(document.scripts, function (s) {
        return /luigis?box/i.test(s.src || '');
      })
    ) {
      reason = 'luigisbox:script';
    }
    if (reason) window.__BS_SEARCH_OFF__ = reason;
    return !!reason;
  } catch (e) {
    return false;
  }
}

function setAll(value, done) {
  resolved = value;
  resolvedDone = done;
  indexResolved = value;
  indexDone = done;
  wholesaleResolved = value;
  wholesaleDone = done;
}

/** Shodí vrstvu na `BREAKER_TTL_MS` (volá `searchApi.js` při 5xx). */
export function tripBreaker() {
  const now = Date.now();
  trippedAt = now;
  try {
    sessionStorage.setItem(BREAKER_KEY, String(now));
  } catch (e) {
    /* blokované úložiště / plná kvóta — TTL drží paměť (`trippedAt`) */
  }
  setAll(null, true);
}

/**
 * Čas shození uložený v sessionStorage: `null` = klíč není nebo úložiště nejde
 * přečíst, `0` = nesmysl v klíči (čte se jako prošlé), jinak ms.
 */
function storedTripAt() {
  let raw;
  try {
    raw = sessionStorage.getItem(BREAKER_KEY);
  } catch (e) {
    return null;
  }
  if (raw === null) return null;
  const at = Number(raw);
  return Number.isFinite(at) && at > 0 ? at : 0;
}

/**
 * `true` = shozeno a TTL neuplynulo — rozhoduje novější z času v paměti a v
 * úložišti, takže nečitelné nebo chybějící úložiště čerstvé shození NEzruší.
 * Prošlé TTL uklidí obojí a zapne jeden zkušební GET bez HTTP cache — cache
 * `/search` má max-age 60 s < TTL, takže cached 200 živé API nepředstírá, ale
 * `no-store` je levná jistota.
 */
function breakerTripped() {
  const stored = storedTripAt();
  const at = Math.max(trippedAt, stored || 0);
  if (at > 0 && Date.now() - at < BREAKER_TTL_MS) {
    trippedAt = at;
    return true;
  }
  if (!trippedAt && stored === null) return false; // nikdy neshozeno
  trippedAt = 0;
  try {
    sessionStorage.removeItem(BREAKER_KEY);
  } catch (e) {
    /* blokované úložiště */
  }
  probePending = true;
  return false;
}

/**
 * Cache guardů drží `null` kvůli breakeru → po uplynutí TTL se všechny tři
 * konfigurace přepočítají (jinak by `getConfig()` zůstal `null` až do
 * `resetConfig()`, tedy do další stránky).
 */
function recoverIfExpired() {
  if (!trippedAt) return;
  if (breakerTripped()) return;
  setAll(undefined, false);
}

/** Jednorázově `true` po obnově z breakeru — první GET jde s `cache: 'no-store'`. */
export function takeBreakerProbe() {
  const p = probePending;
  probePending = false;
  return p;
}

/** Ruční override (staging, sondy, rychlé přepnutí bez deploye) — koncepce §Dvě domény. */
function overrideConfig() {
  const override = window.__BS_SEARCH__;
  return override && override.tenant ? override : null;
}

function resolveIndex() {
  if (unsupported() || offlineFixture() || breakerTripped() || luigisBoxPresent()) return null;

  const override = overrideConfig();
  if (override) {
    return { api: override.api || API, tenant: override.tenant, language: (shoptetData() || {}).language || 'cs' };
  }

  const data = shoptetData();
  if (!data) return null;

  const tenant = TENANTS[data.projectId];
  if (!tenant) return null; // neznámý projectId = vrstva vypnutá

  return { api: API, tenant: tenant, language: data.language || 'cs' };
}

/**
 * Konfigurace pro data z indexu, která NENESOU CENU: samolepka Bestseller, spec
 * chipy, pořadí kategorií na homepage (`/specs` bez cenových polí,
 * `/category-rank`), nebo `null`.
 *
 * Všechny guardy `getConfig()` KROMĚ ceníku zákazníka (2026-09-14): Bestseller je
 * tržba za 90 dní, parametry jsou parametry — velkoobchodu je ukázat můžeme, jen
 * nesmí vidět maloobchodní cenu jako svou. Kdo z odpovědi kreslí cenu (karty
 * `/popular`, hledání), ptá se `getConfig()` a posílá jeho `priceListId`; konec
 * akční ceny `getRetailConfig()`.
 */
export function getIndexConfig() {
  recoverIfExpired();
  if (!indexDone) {
    indexResolved = resolveIndex();
    indexDone = true;
  }
  return indexResolved;
}

/**
 * Velkoobchodní ceníky, jejichž cenu index nese (`price_vat_pl<id>`, search
 * indexer build-doc `voPriceFields`, API `pl`). Musí sedět se
 * `search/indexer/schema.mjs VO_PRICELIST_IDS`. Hodnoty = `search.priceLists`
 * z manifestu shopu (hlavní ceník 1 první, validátor to hlídá).
 */
export const INDEX_PRICE_LISTS = SHOP.search.priceLists;

/**
 * Ceník zákazníka, pro který má index cenu, nebo `null`. Ceník 1 = nepřihlášený
 * i přihlášený maloobchod; VO ceník jen přihlášenému. `priceRatio` ≠ 1 (procentní
 * sleva zákaznické skupiny) index nenese pro žádný ceník.
 */
function indexPriceList(customer) {
  const c = customer || {};
  if (Number(c.priceRatio) !== 1) return null;
  const id = Number(c.priceListId);
  if (id === 1) return 1;
  if (c.registered === true && INDEX_PRICE_LISTS.indexOf(id) > 0) return id;
  return null;
}

function resolve() {
  const index = getIndexConfig();
  if (!index) return null;

  // Zobrazit velkoobchodnímu zákazníkovi maloobchodní cenu je horší než horší
  // relevance (koncepce §Frontend, bod 3) → jen ceníky, které index nese
  // (od 2026-09-14 i VO 13/16/19), jiné = vrstva vypnutá. API na staré generaci
  // indexu bez VO polí vrací 400 → nativní výsledky. Override je test tenantu,
  // ceníkem zákazníka se neřídí.
  let priceListId = 1;
  if (!overrideConfig()) {
    priceListId = indexPriceList((shoptetData() || {}).customer);
    if (priceListId === null) return null;
  }

  return { api: index.api, tenant: index.tenant, language: index.language, priceListId: priceListId };
}

/**
 * Konfigurace vrstvy s CENAMI z indexu (vyhledávání, našeptávač, řada `/popular`,
 * analytika hledání), nebo `null` když se nemá spustit. `priceListId` = ceník,
 * v jehož cenách API vrací karty (`pl`). Konec akční ceny se ptá
 * `getRetailConfig()` — akce v indexu patří jen ceníku 1.
 * Výsledek se cachuje — guardy se během jedné stránky nemění (breaker si
 * cache shazuje sám).
 */
export function getConfig() {
  recoverIfExpired();
  if (!resolvedDone) {
    resolved = resolve();
    resolvedDone = true;
  }
  return resolved;
}

/**
 * `getConfig()` jen pro ceník 1, jinak `null`. Pro to, co z indexu kreslí AKCI
 * ceníku 1 (konec akční ceny u ceny): VO ceníky akce nemají a akce ceníku 1
 * pro velkoobchod neplatí.
 */
export function getRetailConfig() {
  const cfg = getConfig();
  return cfg && cfg.priceListId === 1 ? cfg : null;
}

function resolveWholesale() {
  if (unsupported() || offlineFixture() || breakerTripped()) return null;
  const data = shoptetData() || {};
  const customer = data.customer || {};
  if (customer.registered !== true) return null;
  const priceListId = Number(customer.priceListId);
  if (!Number.isFinite(priceListId) || priceListId === 1) return null;

  const override = window.__BS_SEARCH__;
  if (override && override.tenant) {
    return { api: override.api || API, tenant: override.tenant, language: data.language || 'cs', priceListId: priceListId };
  }
  const tenant = TENANTS[data.projectId];
  if (!tenant) return null;
  return { api: API, tenant: tenant, language: data.language || 'cs', priceListId: priceListId };
}

/**
 * Konfigurace pro dotaz na BĚŽNÉ ceny (`/specs` → `prices`) přihlášenému
 * zákazníkovi s jiným než hlavním ceníkem (velkoobchod VO1–VO3), nebo `null`.
 *
 * Záměrně BEZ guardu ceníku z `getConfig()`: ten chrání výsledky hledání
 * (velkoobchod nesmí vidět maloobchodní cenu jako svou), kdežto tady se běžná
 * cena ukazuje právě jako srovnání vedle jeho vlastní ceny (wholesalePrice.js).
 * Bez stand-down guardu Luigi's Boxu — ten se týká vyhledávání, ne cen. Ostatní
 * guardy platí stejně: starý prohlížeč, offline fixture, shozený breaker,
 * neznámý shop.
 */
export function getWholesaleConfig() {
  recoverIfExpired();
  if (!wholesaleDone) {
    wholesaleResolved = resolveWholesale();
    wholesaleDone = true;
  }
  return wholesaleResolved;
}

/**
 * Konfigurace stránky Stav objednávky (`POST /v1/{tenant}/order-status`, #461),
 * nebo `null`. Jen guardy, bez kterých dotaz nejde napsat (starý prohlížeč,
 * offline fixture) a neznámý shop: breaker hledání, Luigi's Box ani ceník
 * zákazníka se lookupu netýkají (vlastní klient `orderStatusApi.js`, výpadek
 * hledání stav objednávky nevypíná). `window.__BS_SEARCH__` přepne tenant
 * i tady (staging, sondy).
 */
export function getOrderStatusConfig() {
  if (unsupported() || offlineFixture()) return null;
  const override = overrideConfig();
  if (override) return { api: override.api || API, tenant: override.tenant };
  const data = shoptetData();
  const tenant = data && TENANTS[data.projectId];
  return tenant ? { api: API, tenant: tenant } : null;
}

/** Jen pro testy a přepnutí hladiny po přihlášení. */
export function resetConfig() {
  setAll(undefined, false);
  trippedAt = 0;
  probePending = false;
}
